import { z } from 'zod';
import { zfd } from 'zod-form-data';
import { decorationSchema, decorationSchemaServer } from '../decoration';
import { Merch } from './schema';

export enum TeamNameOption {
	TeamName = 'teamName',
	TeamNumber = 'teamNumber',
	FirstName = 'firstName',
	LastName = 'lastName',
	FullName = 'fullName',
}

interface TeamNameOptionList {
	id: TeamNameOption;
	label: string;
	initialValue: string;
	defaultSize?: number;
	maxHeightPercentage?: number;
	cardStyles: Record<string, number>;
	position?: { x: number; y: number };
	preview?: { image: { url: string } };
}

export function mapTeamNameOptionsToList(options: Array<any>): TeamNameOptionList[] {
	return options.map(option => ({
		id: option.id as TeamNameOption,
		label: option.label,
		initialValue: option.initialValue,
		defaultSize: option.defaultHeightMM ?? undefined,
		maxHeightPercentage: option.maxHeightPercentage ?? undefined,
		cardStyles: { fontSize: option.cardFontSize, lineHeight: option.cardLineHeight },
		position:
			option.topPosition !== undefined && option.centerPosition !== undefined
				? { x: option.topPosition, y: option.centerPosition }
				: undefined,
		preview: option.preview ?? undefined,
	}));
}

export const teamNameSchema = z
	.object({
		teamNameId: z.string(),
		available: z.array(z.string()),
		selected: z.array(
			z.object({
				size: z.string(),
				quantity: zfd.numeric(),
				inputs: z.array(z.object({ id: z.nativeEnum(TeamNameOption), name: z.string() })),
				decorations: z.array(decorationSchema),
			}),
		),
	})
	.optional();

export type TeamName = z.infer<typeof teamNameSchema>;
export type SelectedTeamName = Exclude<TeamName, undefined>['selected'][number];

export const teamNameServerSchema = z
	.object({
		available: z.array(z.string()),
		selected: z.array(
			z.object({
				size: z.string(),
				quantity: zfd.numeric(),
				inputs: z.array(z.object({ id: z.nativeEnum(TeamNameOption), name: z.string() })),
				decorations: z.array(decorationSchemaServer),
			}),
		),
	})
	.optional();

export type TeamNameServer = z.infer<typeof teamNameServerSchema>;

export function updateTeamNamesWithSize(merch: Merch) {
	const available = merch.teamNames?.available as TeamNameOption[];
	//Make sure that if there are available teamNames, that each size item has a teamName
	if (!!merch.teamNames && !!available?.length) {
		const sizeItems = merch.sizing.reduce((acc, size) => {
			const sizes = new Array(size.quantity).fill(size.size);
			return [...acc, ...sizes];
		}, [] as string[]);

		const teamNamesToCheck = [...merch.teamNames.selected];
		merch.teamNames.selected = sizeItems.map(size => {
			const matchedIndex = teamNamesToCheck.findIndex(teamName => teamName.size === size);

			if (matchedIndex > -1) {
				const existing = teamNamesToCheck[matchedIndex];
				teamNamesToCheck.splice(matchedIndex, 1);
				return existing;
			} else {
				return {
					size,
					quantity: 1,
					inputs: available.map(option => ({
						id: option,
						name: '',
					})),
					decorations: [],
				};
			}
		});
	}
	return merch;
}

export function getEmptyTeamName(size: string, available: string[]): SelectedTeamName {
	return {
		size,
		decorations: [],
		quantity: 1,
		inputs: available.map(option => ({
			id: option as TeamNameOption,
			name: '',
		})),
	};
}

export function getEmptyTeamNames(available: string[], merch: Merch) {
	const sizeItems = merch.sizing.reduce((acc, size) => {
		const sizes = new Array(size.quantity).fill(size.size);
		return [...acc, ...sizes];
	}, [] as string[]);

	return sizeItems.map(size => getEmptyTeamName(size, available));
}

export function getTeamNameState(merch?: Merch) {
	return {
		quantity: merch?.teamNames?.available.length ? merch?.teamNames?.selected.length : 0,
		complete: merch?.teamNames?.selected.filter(s => s.inputs.every(i => i.name)).length || 0,
	};
}
